import React, { createContext, useContext, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { WeatherContextType } from '../types/context';
import { StormReport } from '../types/storm';
import { fetchWeatherData } from '../services/weatherService';
import { useAuth } from '../hooks/useAuth';

const WeatherContext = createContext<WeatherContextType | undefined>(undefined);

export function WeatherProvider({ children }: { children: React.ReactNode }) {
  const { updateCredits } = useAuth();
  
  // Map state
  const [mapInstance, setMapInstance] = useState<mapboxgl.Map | null>(null);
  const [drawInstance, setDrawInstance] = useState<MapboxDraw | null>(null);

  // Weather state
  const [modelSelect, setModelSelect] = useState('openmeteo');
  const [parameterSelect, setParameterSelect] = useState('temperature_2m_max');
  const [currentMode, setCurrentMode] = useState('polygon');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [weatherData, setWeatherData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState<{ current: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolution, setResolution] = useState(0.0125);
  
  // Storm report state
  const [stormFilter, setStormFilter] = useState('all');
  const [hailThreshold, setHailThreshold] = useState(1.0);
  const [triggerOperator, setTriggerOperator] = useState('>');
  const [triggerValue, setTriggerValue] = useState(1.0);
  const [showPayoutTable, setShowPayoutTable] = useState(false);
  const [selectedReport, setSelectedReport] = useState<StormReport | null>(null);

  // Drought state
  const [selectedState, setSelectedState] = useState<string>('');

  const fetchData = async () => {
    if (!mapInstance || !drawInstance) {
      throw new Error('Map or draw instance not initialized');
    }

    setLoading(true);
    setError(null);
    setLoadingProgress(null);

    try {
      const result = await fetchWeatherData({
        model: modelSelect,
        parameter: parameterSelect,
        startDate,
        endDate,
        stormFilter,
        hailThreshold,
        mapInstance,
        drawInstance,
        resolution,
        selectedState,
        onCreditUpdate: updateCredits,
        setLoadingProgress: setLoadingProgress
      });

      setWeatherData(result);
    } catch (error) {
      console.error('Weather service error:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch weather data');
      setWeatherData([]);
    } finally {
      setLoading(false);
      setLoadingProgress(null);
    }
  };

  const clearData = () => {
    if (drawInstance) {
      drawInstance.deleteAll();
    }

    if (mapInstance) {
      mapInstance.flyTo({
        center: [-0.085, 51.512],
        zoom: 14,
        duration: 1000
      });
    }

    setWeatherData([]);
    setError(null);
    setLoadingProgress(null);
    setSelectedReport(null);
    setSelectedState('');
    
    if (mapInstance?.getStyle()) {
      ['openmeteo-grid', 'stormreports-layer', 'drought-layer'].forEach(layerId => {
        if (mapInstance.getLayer(layerId)) {
          mapInstance.removeLayer(layerId);
        }
      });
      
      ['openmeteo-data', 'stormreports-data', 'drought-data'].forEach(sourceId => {
        if (mapInstance.getSource(sourceId)) {
          mapInstance.removeSource(sourceId);
        }
      });
    }
  };

  const value = {
    mapInstance,
    drawInstance,
    setMapInstance,
    setDrawInstance,
    modelSelect,
    setModelSelect,
    parameterSelect,
    setParameterSelect,
    currentMode,
    setCurrentMode,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    weatherData,
    setWeatherData,
    loading,
    setLoading,
    loadingProgress,
    setLoadingProgress,
    error,
    setError,
    stormFilter,
    setStormFilter,
    hailThreshold,
    setHailThreshold,
    triggerOperator,
    setTriggerOperator,
    triggerValue,
    setTriggerValue,
    showPayoutTable,
    setShowPayoutTable,
    resolution,
    setResolution,
    selectedReport,
    setSelectedReport,
    selectedState,
    setSelectedState,
    fetchData,
    clearData
  };

  return (
    <WeatherContext.Provider value={value}>
      {children}
    </WeatherContext.Provider>
  );
}

export function useWeather() {
  const context = useContext(WeatherContext);
  if (!context) {
    throw new Error('useWeather must be used within a WeatherProvider');
  }
  return context;
}